import React, { useEffect, useState } from "react";
import { Card, Label, Input, Table } from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { isEmpty } from "lodash";
import { toast } from "react-toastify";
import Select from "react-select";
import LoadingSpinner from "../../../../Components/Common/Spinner";
import {
  thirdPartyBrightPaySaveCredentials,
  thirdPartyBrightPaySaveCredentialsSuccess,
  thirdPartyBrightPayUrlAuth,
  thirdPartyBrightPayUrlAuthSuccess,
  thirdPartyBrightPayCheckStatus,
  thirdPartyBrightPayGenerateAccessToken,
  thirdPartyBrightPayAssignEmployer,
  thirdPartyBrightPayUnassign,
  thirdPartyBrightPayUnassignSuccess,
  thirdPartyBrightPaySyncEmployees,
  thirdPartyBrightPaySyncEmployeesSuccess,
  thirdPartyBrightPayEmployeeList,
} from "../../../../store/thirdPartyIntegration/action";

// BrightPay is per-company for BOTH tokens AND client credentials: the company
// pastes its own Client ID/Secret (saved via savecredentials) before connecting.
// Mirrors the Xero tab but the connect URL is fetched from the backend (it is
// built from THIS company's stored client_id) rather than hardcoded here.
export default function BrightPayIntegration() {
  const dispatch = useDispatch();
  const params = useParams();
  const codeParam = params?.code;

  const [extractedCode, setExtractedCode] = useState(codeParam || null);
  const [editCredentials, setEditCredentials] = useState(false);
  const [credentials, setCredentials] = useState({
    ClientId: "",
    ClientSecret: "",
  });
  const [assignEmployer, setAssignEmployer] = useState(false);
  const [employers, setEmployers] = useState([]);
  const [selectedEmployer, setSelectedEmployer] = useState(null);
  const [syncInterval, setSyncInterval] = useState("4");

  const {
    checkStatusDataAPI,
    checkStatusLoading,
    saveCredentialsDataAPI,
    urlAuthDataAPI,
    generateAccessTokenDataAPI,
    assignEmployerDataAPI,
    unassignDataAPI,
    syncEmployeesDataAPI,
    employeeListDataAPI,
  } = useSelector((state) => ({
    checkStatusDataAPI:
      state.ThirdPartyIntegration.thirdPartyBrightPayCheckStatusData,
    checkStatusLoading:
      state.ThirdPartyIntegration.thirdPartyBrightPayCheckStatusLoading,
    saveCredentialsDataAPI:
      state.ThirdPartyIntegration.thirdPartyBrightPaySaveCredentialsData,
    urlAuthDataAPI: state.ThirdPartyIntegration.thirdPartyBrightPayUrlAuthData,
    generateAccessTokenDataAPI:
      state.ThirdPartyIntegration.thirdPartyBrightPayGenerateAccessTokenData,
    assignEmployerDataAPI:
      state.ThirdPartyIntegration.thirdPartyBrightPayAssignEmployerData,
    unassignDataAPI:
      state.ThirdPartyIntegration.thirdPartyBrightPayUnassignData,
    syncEmployeesDataAPI:
      state.ThirdPartyIntegration.thirdPartyBrightPaySyncEmployeesData,
    employeeListDataAPI:
      state.ThirdPartyIntegration.thirdPartyBrightPayEmployeeListData,
  }));

  const status = checkStatusDataAPI?.brightpay_status || {};

  const currentDomainName =
    window.location.hostname === "localhost"
      ? "localhost:3000"
      : window.location.hostname;
  const RedirectUrl =
    (window.location.protocol === "https:" ? "https://" : "http://") +
    currentDomainName +
    "/settings/1/27/brightpay/code";

  const syncIntervalOptions = [
    { label: "Every Hour", value: "1" },
    { label: "Every 4 Hours", value: "2" },
    { label: "Every 6 Hours", value: "3" },
    { label: "Every 12 Hours", value: "4" },
    { label: "Every 24 Hours", value: "5" },
  ];

  useEffect(() => {
    dispatch(thirdPartyBrightPayCheckStatus());
  }, []);

  // OAuth callback: exchange the code for tokens, then list employers.
  useEffect(() => {
    if (!extractedCode) return;
    dispatch(
      thirdPartyBrightPayGenerateAccessToken([
        { Code: extractedCode, RedirectUrl },
      ])
    );
  }, [extractedCode]);

  useEffect(() => {
    if (isEmpty(saveCredentialsDataAPI)) return;
    if (saveCredentialsDataAPI.status === 200) {
      toast.success(saveCredentialsDataAPI?.message);
      setEditCredentials(false);
      dispatch(thirdPartyBrightPaySaveCredentialsSuccess([]));
      dispatch(thirdPartyBrightPayCheckStatus());
    } else {
      toast.error(saveCredentialsDataAPI?.message || saveCredentialsDataAPI?.error);
      dispatch(thirdPartyBrightPaySaveCredentialsSuccess([]));
    }
  }, [saveCredentialsDataAPI]);

  // Connect: backend returns the authorize URL built from our stored client_id.
  useEffect(() => {
    if (isEmpty(urlAuthDataAPI)) return;
    if (urlAuthDataAPI.status === 200 && urlAuthDataAPI.url_auth) {
      dispatch(thirdPartyBrightPayUrlAuthSuccess([]));
      window.location.href = urlAuthDataAPI.url_auth;
    } else {
      toast.error(urlAuthDataAPI?.error || "Could not start BrightPay connection");
      dispatch(thirdPartyBrightPayUrlAuthSuccess([]));
    }
  }, [urlAuthDataAPI]);

  useEffect(() => {
    if (isEmpty(generateAccessTokenDataAPI)) return;
    if (generateAccessTokenDataAPI.status === 200) {
      toast.success(generateAccessTokenDataAPI?.message);
      setExtractedCode(null);
      setEmployers(generateAccessTokenDataAPI?.employers || []);
      setAssignEmployer(true);
    } else {
      toast.error(generateAccessTokenDataAPI?.message);
      setExtractedCode(null);
    }
  }, [generateAccessTokenDataAPI]);

  useEffect(() => {
    if (isEmpty(assignEmployerDataAPI)) return;
    if (assignEmployerDataAPI.status === 200) {
      toast.success(assignEmployerDataAPI?.message);
      setAssignEmployer(false);
      dispatch(thirdPartyBrightPayCheckStatus());
    } else {
      toast.error(assignEmployerDataAPI?.message);
    }
  }, [assignEmployerDataAPI]);

  useEffect(() => {
    if (isEmpty(unassignDataAPI)) return;
    if (unassignDataAPI.status === 200) {
      toast.success(unassignDataAPI?.message);
      dispatch(thirdPartyBrightPayUnassignSuccess([]));
      dispatch(thirdPartyBrightPayCheckStatus());
    } else {
      toast.error(unassignDataAPI?.message);
      dispatch(thirdPartyBrightPayUnassignSuccess([]));
    }
  }, [unassignDataAPI]);

  useEffect(() => {
    if (isEmpty(syncEmployeesDataAPI)) return;
    if (syncEmployeesDataAPI.status === 200) {
      const s = syncEmployeesDataAPI?.sync || {};
      toast.success(
        `Sync complete: ${s.linked ?? 0} newly linked, ${s.matched ?? 0} matched of ${s.total ?? 0}`
      );
      dispatch(thirdPartyBrightPaySyncEmployeesSuccess([]));
      dispatch(thirdPartyBrightPayEmployeeList());
    } else {
      toast.error(syncEmployeesDataAPI?.error || "BrightPay sync failed");
      dispatch(thirdPartyBrightPaySyncEmployeesSuccess([]));
    }
  }, [syncEmployeesDataAPI]);

  const handleSaveCredentials = () => {
    if (!credentials.ClientId || !credentials.ClientSecret) {
      toast.error("Enter both Client ID and Client Secret");
      return;
    }
    dispatch(thirdPartyBrightPaySaveCredentials([credentials]));
  };

  const handleConnect = () => {
    dispatch(thirdPartyBrightPayUrlAuth());
  };

  const handleAssign = () => {
    if (!selectedEmployer) {
      toast.error("Select an employer");
      return;
    }
    dispatch(
      thirdPartyBrightPayAssignEmployer([
        {
          EmployerId: selectedEmployer.employerId,
          OrganisationId: selectedEmployer.organisationId,
          EmployerName: selectedEmployer.employerNameWithHint,
          SyncInterval: Number(syncInterval),
        },
      ])
    );
  };

  const handleDisconnect = () => {
    dispatch(thirdPartyBrightPayUnassign());
  };

  const handleSyncNow = () => {
    dispatch(thirdPartyBrightPaySyncEmployees());
  };

  const handleViewEmployees = () => {
    dispatch(thirdPartyBrightPayEmployeeList());
  };

  if (checkStatusLoading) return <LoadingSpinner />;

  return (
    <Card className="p-3">
      <h5 className="mb-3">BrightPay Payroll</h5>

      {extractedCode ? (
        <h6>Processing please wait...</h6>
      ) : assignEmployer ? (
        <>
          <p className="text-muted">
            Choose which BrightPay employer to sync employees from.
          </p>
          <Label>Employer</Label>
          <Select
            placeholder="Select employer"
            options={employers.map((e) => ({
              label: e?.employerNameWithHint,
              value: e?.employerId,
            }))}
            value={
              selectedEmployer
                ? {
                    label: selectedEmployer.employerNameWithHint,
                    value: selectedEmployer.employerId,
                  }
                : null
            }
            onChange={(opt) =>
              setSelectedEmployer(
                employers.find((e) => e.employerId === opt.value) || null
              )
            }
          />
          <Label className="mt-2">Sync Interval</Label>
          <Select
            options={syncIntervalOptions}
            value={syncIntervalOptions.find((o) => o.value === syncInterval)}
            onChange={(opt) => setSyncInterval(opt.value)}
          />
          <button
            className="btn btn-success w-100 mt-3"
            onClick={handleAssign}
          >
            Connect &amp; Sync Employees
          </button>
        </>
      ) : editCredentials || status?.has_credentials !== 1 ? (
        <>
          <p className="text-muted">
            Enter the Client ID and Secret from your BrightPay API application.
            Each company connects with its own BrightPay credentials.
          </p>
          <Label>Client ID</Label>
          <Input
            type="text"
            value={credentials.ClientId}
            onChange={(e) =>
              setCredentials({ ...credentials, ClientId: e.target.value })
            }
          />
          <Label className="mt-2">Client Secret</Label>
          <Input
            type="password"
            value={credentials.ClientSecret}
            onChange={(e) =>
              setCredentials({ ...credentials, ClientSecret: e.target.value })
            }
          />
          <div className="d-flex gap-2 mt-3">
            <button className="btn btn-primary" onClick={handleSaveCredentials}>
              Save Credentials
            </button>
            {status?.has_credentials === 1 && (
              <button
                className="btn btn-light"
                onClick={() => setEditCredentials(false)}
              >
                Cancel
              </button>
            )}
          </div>
        </>
      ) : status?.connected !== 1 ? (
        <>
          <p className="text-muted">
            Credentials saved. Connect your BrightPay account to authorise access.
          </p>
          <div className="d-flex gap-2">
            <button className="btn btn-success" onClick={handleConnect}>
              Connect to BrightPay
            </button>
            <button
              className="btn btn-light"
              onClick={() => setEditCredentials(true)}
            >
              Update Credentials
            </button>
          </div>
        </>
      ) : (
        <>
          <p className="mb-1">
            <strong>Connected employer:</strong>{" "}
            {status?.employer_name || "(selected)"}
          </p>
          <p className="mb-1 text-muted">
            Last sync: {status?.last_sync || "never"}
          </p>
          <div className="d-flex gap-2 my-2 flex-wrap">
            <button className="btn btn-success" onClick={handleSyncNow}>
              Sync Now
            </button>
            <button className="btn btn-light" onClick={handleViewEmployees}>
              View Linked Employees
            </button>
            <button className="btn btn-danger" onClick={handleDisconnect}>
              Disconnect
            </button>
          </div>

          {!isEmpty(employeeListDataAPI?.brightpay_employee_list) && (
            <Table bordered responsive className="mt-2">
              <thead>
                <tr>
                  <th>Staff</th>
                  <th>BrightPay Employee ID</th>
                </tr>
              </thead>
              <tbody>
                {employeeListDataAPI.brightpay_employee_list.map((row) => (
                  <tr key={row.Id}>
                    <td>{row.Staff_FullName || "(unmatched)"}</td>
                    <td>{row.ExternalIdentification}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </>
      )}
    </Card>
  );
}
